/**
 * Settings Index Page (Story 6-6-6, Story 10-5-8)
 * Provides navigation to various settings pages (Profile, Security).
 * Staff users can also reset their view preference (delivery vs operations).
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../../store/authStore";
import { Card } from "../../components/molecules/Card";
import { UserIcon, ShieldCheckIcon, EyeIcon, GlobeAltIcon } from "@heroicons/react/24/outline";
import { Button } from "../../components/atoms/Button";

export function Settings() {
  const { t } = useTranslation("settings");
  const { user } = useAuthStore();
  const currentViewPref = localStorage.getItem("staff_view_preference") as
    | "delivery"
    | "operations"
    | null;
  const currentViewLabel =
    currentViewPref === "delivery"
      ? t("delivery")
      : currentViewPref === "operations"
        ? t("operations")
        : t("not_set");

  const handleResetViewPreference = () => {
    localStorage.removeItem("staff_view_preference");
    window.location.href = "/";
  };

  return (
    <div className="max-w-2xl mx-auto py-6 px-4">
      <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">{t("title")}</h1>

      <div className="space-y-4">
        {/* Profile Card */}
        <Link to="/settings/profile" className="block">
          <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                <UserIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  {t("profile_title")}
                </h2>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  {t("profile_description")}
                </p>
              </div>
            </div>
          </Card>
        </Link>

        {/* Security Card */}
        <Link to="/settings/security" className="block">
          <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center flex-shrink-0">
                <ShieldCheckIcon className="w-6 h-6 text-success-600 dark:text-success-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  {t("security_title")}
                </h2>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  {t("security_description")}
                </p>
              </div>
            </div>
          </Card>
        </Link>

        {/* Language Card (Story 11.2) */}
        <Link to="/settings/language" className="block">
          <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                <GlobeAltIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  {t("language_title")}
                </h2>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  {t("language_description")}
                </p>
              </div>
            </div>
          </Card>
        </Link>

        {/* View Preference Card - Staff only (Story 10-5-8) */}
        {user?.role === "staff" && (
          <Card className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-secondary-100 dark:bg-secondary-900/30 flex items-center justify-center flex-shrink-0">
                <EyeIcon className="w-6 h-6 text-secondary-600 dark:text-secondary-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  {t("view_preference_title")}
                </h2>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                  {t("current_view")} <span className="font-semibold">{currentViewLabel}</span>
                </p>
                <Button onClick={handleResetViewPreference} variant="outline" size="sm">
                  {t("reset_view_preference")}
                </Button>
                <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-500">
                  {t("reset_view_preference_hint")}
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
